import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import productService from '../services/productService';
import { formatLocation } from '../utils/locationFormat';

class ProductStore {
  products = [];
  categories = [];
  cities = [];
  selectedProduct = null;
  loading = false;
  error = null;

  // Filters
  filters = {
    category: null,
    animalCategory: null,
    city: null,
    search: '',
    minPrice: null,
    maxPrice: null,
    minRating: null, // 1, 2, 3, 4, 5
    sortBy: 'name', // name, price_asc, price_desc, rating_desc
    partner: null, // Filter by partner ID
  };

  // Pagination
  pagination = {
    page: 1,
    pageSize: 12,
    total: 0,
  };

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      products: observable,
      categories: observable,
      cities: observable,
      selectedProduct: observable,
      loading: observable,
      error: observable,
      filters: observable,
      pagination: observable,

      filteredProducts: computed,

      setProducts: action,
      setCategories: action,
      setCities: action,
      setSelectedProduct: action,
      setLoading: action,
      setError: action,
      updateFilter: action,
      resetFilters: action,
      fetchProducts: action,
      fetchCategories: action,
      fetchCities: action,
      fetchProductById: action,
    });
  }

  get filteredProducts() {
    let filtered = [...this.products];

    // Apply category filter
    if (this.filters.category) {
      filtered = filtered.filter(p => p.category?.id === this.filters.category);
    }

    // Apply animal category filter
    if (this.filters.animalCategory) {
      filtered = filtered.filter(p =>
        p.animal_categories?.some(ac => ac.id === this.filters.animalCategory)
      );
    }

    // Apply city filter (case-insensitive)
    if (this.filters.city) {
      filtered = filtered.filter(p =>
        p.partner_city?.toLowerCase() === this.filters.city?.toLowerCase()
      );
    }

    // Note: Partner filter is handled by backend, no need for client-side filtering

    // Apply search filter
    if (this.filters.search) {
      const searchLower = this.filters.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.title?.toLowerCase().includes(searchLower) ||
        p.title_en?.toLowerCase().includes(searchLower) ||
        p.title_el?.toLowerCase().includes(searchLower) ||
        p.name?.toLowerCase().includes(searchLower) ||
        // Search in nested category object
        p.category?.name?.toLowerCase().includes(searchLower) ||
        p.category?.name_en?.toLowerCase().includes(searchLower) ||
        p.category?.name_el?.toLowerCase().includes(searchLower) ||
        p.description_en?.toLowerCase().includes(searchLower) ||
        p.description_el?.toLowerCase().includes(searchLower)
      );
    }

    // Apply price filters
    if (this.filters.minPrice !== null) {
      filtered = filtered.filter(p => {
        const minPrice = Math.min(...(p.variants?.flatMap(v =>
          v.inventory?.map(i => parseFloat(i.price)) || []
        ) || [Infinity]));
        return minPrice >= this.filters.minPrice;
      });
    }

    if (this.filters.maxPrice !== null) {
      filtered = filtered.filter(p => {
        const maxPrice = Math.max(...(p.variants?.flatMap(v =>
          v.inventory?.map(i => parseFloat(i.price)) || []
        ) || [0]));
        return maxPrice <= this.filters.maxPrice;
      });
    }

    // Apply rating filter
    if (this.filters.minRating !== null) {
      filtered = filtered.filter(p =>
        parseFloat(p.average_rating || 0) >= this.filters.minRating
      );
    }

    // Apply sorting
    switch (this.filters.sortBy) {
      case 'price_asc':
        filtered.sort((a, b) => {
          const minA = Math.min(...(a.variants?.flatMap(v =>
            v.inventory?.map(i => parseFloat(i.price)) || []
          ) || [Infinity]));
          const minB = Math.min(...(b.variants?.flatMap(v =>
            v.inventory?.map(i => parseFloat(i.price)) || []
          ) || [Infinity]));
          return minA - minB;
        });
        break;
      case 'price_desc':
        filtered.sort((a, b) => {
          const maxA = Math.max(...(a.variants?.flatMap(v =>
            v.inventory?.map(i => parseFloat(i.price)) || []
          ) || [0]));
          const maxB = Math.max(...(b.variants?.flatMap(v =>
            v.inventory?.map(i => parseFloat(i.price)) || []
          ) || [0]));
          return maxB - maxA;
        });
        break;
      case 'rating_desc':
        filtered.sort((a, b) =>
          parseFloat(b.average_rating || 0) - parseFloat(a.average_rating || 0)
        );
        break;
      case 'name':
      default:
        filtered.sort((a, b) =>
          (a.title || '').localeCompare(b.title || '')
        );
    }

    return filtered;
  }

  setProducts(products) {
    this.products = products;
  }

  setCategories(categories) {
    this.categories = categories;
  }

  setCities(cities) {
    this.cities = cities;
  }

  setSelectedProduct(product) {
    this.selectedProduct = product;
  }

  setLoading(loading) {
    this.loading = loading;
  }

  setError(error) {
    this.error = error;
  }

  updateFilter(key, value) {
    this.filters[key] = value;
    // Reset to first page when filters change
    if (key !== 'sortBy') {
      this.pagination.page = 1;
    }
  }

  resetFilters() {
    this.filters = {
      category: null,
      animalCategory: null,
      city: null,
      search: '',
      minPrice: null,
      maxPrice: null,
      minRating: null,
      sortBy: 'name',
      partner: null,
    };
    this.pagination.page = 1;
  }

  async fetchProducts(params = {}) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await productService.getProducts(params);

      runInAction(() => {
        this.products = response.data.results || response.data;

        // Update pagination if available
        if (response.data.count !== undefined) {
          this.pagination.total = response.data.count;
        } else {
          this.pagination.total = this.products.length;
        }

        this.setLoading(false);
      });

      return { success: true };
    } catch (error) {
      runInAction(() => {
        this.setError(error.response?.data?.message || 'Failed to fetch products');
        this.setLoading(false);
      });

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch products'
      };
    }
  }

  async fetchCategories() {
    try {
      const response = await productService.getCategories();

      runInAction(() => {
        this.categories = response.data;
      });

      return { success: true };
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      return { success: false };
    }
  }

  async fetchCities() {
    try {
      const response = await productService.getCities();

      runInAction(() => {
        // Normalize cities and remove duplicates (case-insensitive)
        const cityMap = new Map();
        (response.data || []).forEach(city => {
          if (city) {
            const normalized = formatLocation(city);
            if (!cityMap.has(normalized)) {
              cityMap.set(normalized, normalized);
            }
          }
        });
        this.cities = [...cityMap.values()].sort();
      });

      return { success: true };
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      return { success: false };
    }
  }

  async fetchProductById(id) {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await productService.getProductById(id);

      runInAction(() => {
        this.selectedProduct = response.data;
        this.setLoading(false);
      });

      return { success: true, data: response.data };
    } catch (error) {
      runInAction(() => {
        this.setError(error.response?.data?.message || 'Failed to fetch product');
        this.setLoading(false);
      });

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch product'
      };
    }
  }
}

export default ProductStore;
